<template>
    <b-row v-show="isPublicHoliday(pickedYmd)" class="my-3">
        <b-col cols="12">
            <b-card class="holiday-card text-center p-4 shadow-sm border-0 position-relative overflow-hidden">
                <div class="holiday-sparkle-bg"></div>

                <div class="holiday-icon-badge mb-2">
                    <i class="fa-solid fa-champagne-glasses text-warning"></i>
                </div>

                <div class="d-flex justify-content-center align-items-center flex-wrap gap-2 mb-2">
                    <b-badge variant="danger" class="px-2 py-1 text-uppercase mr-2">
                        <i class="fa-solid fa-calendar-check mr-1"></i>Official Public Holiday
                    </b-badge>
                    <b-badge variant="info" class="px-2 py-1 text-uppercase mr-2">
                        <i class="fa-solid fa-earth-americas mr-1"></i>{{ currentCountryCode }}
                    </b-badge>
                    <b-badge v-if="pickedHoliday && pickedHoliday.fixed !== undefined" variant="secondary" class="px-2 py-1">
                        {{ pickedHoliday.fixed ? 'Fixed Date' : 'Observance Date' }}
                    </b-badge>
                </div>

                <h2 class="holiday-title mb-2">{{ pickedHolidayTitle }}</h2>

                <h5 v-if="pickedHoliday && pickedHoliday.name && pickedHoliday.name !== pickedHoliday.localName"
                    class="text-muted font-italic mb-3">
                    {{ pickedHoliday.name }}
                </h5>

                <div class="holiday-date-pill mb-3">
                    <i class="bi bi-calendar3 mr-2"></i>
                    <strong>{{ formattedHolidayDate }}</strong>
                </div>

                <div class="holiday-info-wrapper mx-auto mt-3">
                    <b-spinner v-if="loadingInfo" small variant="primary" class="my-2"></b-spinner>
                    <p v-else-if="holidayDescription" class="lead holiday-description mb-2">
                        {{ holidayDescription }}
                    </p>
                    <p v-else class="text-muted mb-2">
                        Enjoy the holiday! This day is recognized as an official statutory public holiday.
                    </p>

                    <div v-if="holidaySourceUrl" class="small text-muted mt-2">
                        <a :href="holidaySourceUrl" target="_blank" rel="noopener noreferrer" class="text-info text-decoration-none">
                            <i class="fa-solid fa-arrow-up-right-from-square mr-1"></i>Learn more on Wikipedia
                        </a>
                    </div>
                </div>
            </b-card>
        </b-col>
    </b-row>
</template>

<script>
    export default {
        name: 'PublicHoliday',
        props: {
            pickedYmd: {
                type: String,
                required: false,
                default: ''
            },
            contextYear: {
                type: Number,
                required: false,
                default: null
            },
            countryCode: {
                type: String,
                required: false,
                default: ''
            }
        },
        data() {
            return {
                publicHolidays: {},
                pickedHoliday: null,
                pickedHolidayTitle: '',
                holidayDescription: '',
                holidaySourceUrl: '',
                loadingInfo: false
            };
        },
        computed: {
            currentCountryCode() {
                return this.countryCode || this.$store?.state?.countryCode || 'BG';
            },
            formattedHolidayDate() {
                if (!this.pickedHoliday?.date) return this.pickedYmd || '';
                try {
                    const parts = this.pickedHoliday.date.split('-');
                    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                    return d.toLocaleDateString(undefined, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    });
                } catch {
                    return this.pickedHoliday.date;
                }
            }
        },
        watch: {
            currentCountryCode() {
                // Invalidate cache and reload
                this.publicHolidays = {};
                const year = this.contextYear || (this.pickedYmd ? parseInt(this.pickedYmd.substr(0, 4)) : new Date().getFullYear());
                this.loadPublicHolidays(year);
            },
            contextYear: {
                immediate: true,
                async handler(newYear) {
                    if (newYear) {
                        await this.loadPublicHolidays(newYear);
                    }
                }
            },
            pickedYmd: {
                immediate: true,
                async handler(newPickedYmd) {
                    const pickedYear = newPickedYmd?.substr(0, 4);
                    if (pickedYear) {
                        await this.loadPublicHolidays(pickedYear);
                    }
                    this.onNewPickedYmd(newPickedYmd);
                }
            },
            pickedHoliday: {
                async handler(newPickedHoliday) {
                    if (newPickedHoliday) {
                        this.pickedHolidayTitle = newPickedHoliday.localName || newPickedHoliday.name;
                        await this.fetchHolidayPublicInfo(newPickedHoliday);
                    } else {
                        this.pickedHolidayTitle = '';
                        this.holidayDescription = '';
                        this.holidaySourceUrl = '';
                    }
                }
            }
        },
        methods: {
            async loadPublicHolidays(year) {
                const country = this.currentCountryCode;
                const cacheKey = `${country}_${year}`;
                if (this.publicHolidays[cacheKey]?.length) {
                    return;
                }
                if (this.$store?.state?.publicHolidays?.[cacheKey]?.length) {
                    this.$set(this.publicHolidays, cacheKey, this.$store.state.publicHolidays[cacheKey]);
                    if (this.pickedYmd) {
                        this.onNewPickedYmd(this.pickedYmd);
                    }
                    return;
                }
                try {
                    const resp = await this.$http.get(`/ExternalApi/PublicHolidays/${year}?countryCode=${country}`);
                    const holidays = Array.isArray(resp?.data) ? resp.data : [];
                    this.$set(this.publicHolidays, cacheKey, holidays);
                    if (this.$store) {
                        this.$store.commit('setPublicHolidays', { key: cacheKey, holidays });
                    }
                    if (this.pickedYmd) {
                        this.onNewPickedYmd(this.pickedYmd);
                    }
                    this.$emit('holidays-loaded', { cacheKey, holidays });
                } catch (err) {
                    console.error('Failed to load public holidays:', err);
                }
            },
            async fetchHolidayPublicInfo(holiday) {
                this.loadingInfo = true;
                this.holidayDescription = '';
                this.holidaySourceUrl = '';

                const searchTitle = holiday.name || holiday.localName;
                if (!searchTitle) {
                    this.loadingInfo = false;
                    return;
                }

                try {
                    // Try Wikipedia summary for dynamic public info
                    const cleanTitle = searchTitle.replace(/\s*\(.*?\)\s*/g, '').trim();
                    const url = `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(cleanTitle)}`;
                    const res = await fetch(url);
                    if (res.ok) {
                        const data = await res.json();
                        if (data.extract) {
                            this.holidayDescription = data.extract;
                            this.holidaySourceUrl = data.content_urls?.desktop?.page || '';
                        }
                    }
                } catch (e) {
                    console.debug('Could not fetch dynamic holiday summary from Wikipedia:', e);
                } finally {
                    this.loadingInfo = false;
                }
            },
            onNewPickedYmd(ymd) {
                this.pickedHoliday = this.getPublicHoliday(ymd);
            },
            getPublicHoliday(ymd) {
                const year = ymd?.substr(0, 4);
                if (!year) return null;
                const cacheKey = `${this.currentCountryCode}_${year}`;
                const list = this.publicHolidays[cacheKey] || this.$store?.state?.publicHolidays?.[cacheKey];
                if (!list || !Array.isArray(list)) return null;
                return list.find(ph => ph.date === ymd) || null;
            },
            isPublicHoliday(ymd) {
                return this.getPublicHoliday(ymd) != null;
            }
        }
    };
</script>

<style scoped>
    .holiday-card {
        width: 100%;
        background: linear-gradient(135deg, #fffdf2 0%, #f0f7ff 100%);
        border-radius: 1.25rem;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
    }

    [data-theme="dark"] .holiday-card {
        background: linear-gradient(135deg, #1f2733 0%, #16202c 100%) !important;
        border: 1px solid #2d3748 !important;
    }

    .holiday-icon-badge {
        font-size: 2.2rem;
    }

    .holiday-title {
        font-size: 2.2rem;
        font-weight: 800;
        letter-spacing: -0.02em;
        color: #d63384;
        background: linear-gradient(90deg, #ff6b6b, #f06595, #cc5de8, #845ef7);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
    }

    .holiday-date-pill {
        display: inline-block;
        background-color: rgba(0, 0, 0, 0.05);
        padding: 0.35rem 1.25rem;
        border-radius: 20px;
        font-size: 0.95rem;
        color: #495057;
    }

    [data-theme="dark"] .holiday-date-pill {
        background-color: rgba(255, 255, 255, 0.08);
        color: #cbd5e1;
    }

    .holiday-info-wrapper {
        max-width: 720px;
    }

    .holiday-description {
        font-size: 1.05rem;
        line-height: 1.6;
        color: #334155;
    }

    [data-theme="dark"] .holiday-description {
        color: #cbd5e1;
    }
</style>
